// Organization Users Modal - Manage additional users for an organization
// Allows admins to add multiple users and provision them in batch

import { useState, useEffect } from 'react'
import { n8nApi } from '../../../services/n8nApi'
import { useAuth } from '../../../context/AuthContext'
import UserStatusBadge from '../../../components/UserStatusBadge'

const ROLE_OPTIONS = [
  { value: 'user_admin', label: 'User Admin', description: 'Full user access' },
  { value: 'driver_admin', label: 'Driver Admin', description: 'Driver management' },
  { value: 'viewer', label: 'Viewer', description: 'Read-only access' }
]

// Extract valid emails from text (handles copy-paste from spreadsheets)
const extractEmails = (text) => {
  const emailRegex = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g
  const matches = text.match(emailRegex) || []
  return [...new Set(matches.map(e => e.toLowerCase()))]
}

// Validate single email
const isValidEmail = (email) => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
}

const OrganizationUsersModal = ({ isOpen, onClose, prospect, onUsersChanged }) => {
  const { user, createUserAsAdmin, sendPasswordResetToUser } = useAuth()

  // Users loaded from Airtable
  const [users, setUsers] = useState([])
  const [isLoadingUsers, setIsLoadingUsers] = useState(false)

  // Add users form state
  const [emailInput, setEmailInput] = useState('')
  const [parsedEmails, setParsedEmails] = useState([]) // Emails ready to add
  const [defaultRole, setDefaultRole] = useState('user_admin')
  const [isAdding, setIsAdding] = useState(false)
  const [addError, setAddError] = useState('')

  // Provisioning state
  const [isProvisioning, setIsProvisioning] = useState(false)
  const [provisionProgress, setProvisionProgress] = useState({ current: 0, total: 0, currentEmail: '' })
  const [provisionResults, setProvisionResults] = useState(null)

  // Load users when modal opens
  useEffect(() => {
    if (isOpen && prospect) {
      loadUsers()
    }
  }, [isOpen, prospect])

  // Parse emails as user types
  useEffect(() => {
    const emails = extractEmails(emailInput)
    // Filter out emails that already exist
    const existingEmails = users.map(u => u.email.toLowerCase())
    const newEmails = emails.filter(e => !existingEmails.includes(e))
    setParsedEmails(newEmails)
  }, [emailInput, users])

  const loadUsers = async () => {
    if (!prospect?.firebase_uid) return

    setIsLoadingUsers(true)
    try {
      const response = await n8nApi.getOrganizationUsers(prospect.firebase_uid, user.uid)
      if (response.success) {
        setUsers(response.users || [])
      }
    } catch (err) {
      console.error('Failed to load organization users:', err)
    } finally {
      setIsLoadingUsers(false)
    }
  }

  const handleAddUsers = async () => {
    if (parsedEmails.length === 0) return

    setIsAdding(true)
    setAddError('')

    try {
      // Prepare users with default name (email prefix) and selected role
      const usersToAdd = parsedEmails.map(email => ({
        email,
        full_name: email.split('@')[0], // Default name from email
        role: defaultRole
      }))

      const response = await n8nApi.addOrganizationUsers(
        prospect.firebase_uid,
        usersToAdd,
        user.uid
      )

      if (response.success) {
        setEmailInput('')
        setParsedEmails([])
        await loadUsers()
        onUsersChanged?.()
      } else {
        setAddError(response.error || 'Failed to add users')
      }
    } catch (err) {
      console.error('Failed to add organization users:', err)
      setAddError('An error occurred. Please try again.')
    } finally {
      setIsAdding(false)
    }
  }

  const handleRemoveUser = async (userId) => {
    try {
      const response = await n8nApi.removeOrganizationUser(userId, user.uid)
      if (response.success) {
        await loadUsers()
        onUsersChanged?.()
      }
    } catch (err) {
      console.error('Failed to remove user:', err)
    }
  }

  const handleProvisionAll = async () => {
    const pendingUsers = users.filter(u => u.provisioning_status === 'pending')
    if (pendingUsers.length === 0) return

    setIsProvisioning(true)
    setProvisionProgress({ current: 0, total: pendingUsers.length, currentEmail: '' })
    setProvisionResults(null)

    const results = []

    for (let i = 0; i < pendingUsers.length; i++) {
      const orgUser = pendingUsers[i]
      setProvisionProgress({
        current: i + 1,
        total: pendingUsers.length,
        currentEmail: orgUser.email
      })

      try {
        // Step 1: Create Firebase Auth account
        const tempPassword = crypto.randomUUID().slice(0, 16) + 'Aa1!'
        const firebaseUser = await createUserAsAdmin(orgUser.email, tempPassword)

        // Step 2: Call n8n workflow to provision in FleetVision
        // The workflow will update the org user record with firebase_uid and status
        const provisionResponse = await n8nApi.provisionOrganizationUsers(
          prospect.firebase_uid,
          [orgUser.id],
          user.uid
        )

        // Step 3: Send password reset email
        await sendPasswordResetToUser(orgUser.email)

        results.push({
          email: orgUser.email,
          status: 'completed',
          firebase_uid: firebaseUser.uid
        })
      } catch (err) {
        console.error(`Failed to provision ${orgUser.email}:`, err)
        results.push({
          email: orgUser.email,
          status: 'failed',
          error: err.code === 'auth/email-already-in-use'
            ? 'Email already has an account'
            : (err.message || 'Provisioning failed')
        })
      }
    }

    setProvisionResults({
      total: pendingUsers.length,
      completed: results.filter(r => r.status === 'completed').length,
      failed: results.filter(r => r.status === 'failed').length,
      results
    })

    await loadUsers()
    onUsersChanged?.()
    setIsProvisioning(false)
  }

  const handleRetryUser = async (orgUser) => {
    setIsProvisioning(true)
    setProvisionProgress({ current: 1, total: 1, currentEmail: orgUser.email })

    try {
      const tempPassword = crypto.randomUUID().slice(0, 16) + 'Aa1!'
      const firebaseUser = await createUserAsAdmin(orgUser.email, tempPassword)

      await n8nApi.provisionOrganizationUsers(
        prospect.firebase_uid,
        [orgUser.id],
        user.uid
      )

      await sendPasswordResetToUser(orgUser.email)
      await loadUsers()
      onUsersChanged?.()
    } catch (err) {
      console.error(`Retry failed for ${orgUser.email}:`, err)
    } finally {
      setIsProvisioning(false)
    }
  }

  if (!isOpen) return null

  const pendingCount = users.filter(u => u.provisioning_status === 'pending').length
  const completedCount = users.filter(u => u.provisioning_status === 'completed').length
  const failedCount = users.filter(u => u.provisioning_status === 'failed').length

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />

        <div className="relative bg-white rounded-xl shadow-xl max-w-2xl w-full p-6 animate-fade-in max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-brand-950">
                Organization Users
              </h3>
              <p className="text-sm text-slate-500">
                {prospect?.company_name}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Stats */}
          <div className="flex gap-4 mb-6">
            <div className="flex items-center gap-2 text-sm">
              <span className="w-2 h-2 rounded-full bg-slate-400" />
              <span className="text-slate-600">{pendingCount} pending</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <span className="w-2 h-2 rounded-full bg-green-500" />
              <span className="text-slate-600">{completedCount} completed</span>
            </div>
            {failedCount > 0 && (
              <div className="flex items-center gap-2 text-sm">
                <span className="w-2 h-2 rounded-full bg-red-500" />
                <span className="text-slate-600">{failedCount} failed</span>
              </div>
            )}
          </div>

          {/* Add Users Section */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-6">
            <h4 className="text-sm font-medium text-slate-700 mb-2">
              Add Users
            </h4>
            <p className="text-xs text-slate-500 mb-3">
              Paste email addresses below (one per line, or copy from a spreadsheet)
            </p>

            <textarea
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              placeholder="user1@company.com&#10;user2@company.com&#10;user3@company.com"
              rows={4}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 resize-none font-mono"
              disabled={isAdding || isProvisioning}
            />

            {parsedEmails.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {parsedEmails.map(email => (
                  <span key={email} className="inline-flex items-center px-2 py-0.5 rounded bg-brand-100 text-brand-700 text-xs">
                    {email}
                  </span>
                ))}
              </div>
            )}

            <div className="flex items-center gap-3 mt-3">
              <select
                value={defaultRole}
                onChange={(e) => setDefaultRole(e.target.value)}
                className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                disabled={isAdding || isProvisioning}
              >
                {ROLE_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>

              <button
                onClick={handleAddUsers}
                disabled={parsedEmails.length === 0 || isAdding || isProvisioning}
                className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium flex items-center gap-2"
              >
                {isAdding ? (
                  <>
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Adding...
                  </>
                ) : (
                  <>
                    Add {parsedEmails.length > 0 ? `${parsedEmails.length} User${parsedEmails.length > 1 ? 's' : ''}` : 'Users'}
                  </>
                )}
              </button>
            </div>

            {addError && (
              <p className="mt-2 text-sm text-red-600">{addError}</p>
            )}
          </div>

          {/* Provision Results */}
          {provisionResults && (
            <div className={`mb-4 p-3 rounded-lg border ${provisionResults.failed > 0 ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'}`}>
              <p className={`text-sm font-medium ${provisionResults.failed > 0 ? 'text-amber-800' : 'text-green-800'}`}>
                Provisioning complete: {provisionResults.completed} succeeded, {provisionResults.failed} failed
              </p>
              {provisionResults.failed > 0 && (
                <p className="text-xs text-amber-700 mt-1">
                  Failed users can be retried individually below.
                </p>
              )}
            </div>
          )}

          {/* Users List */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-medium text-slate-700">
                Users ({users.length})
              </h4>
              {pendingCount > 0 && (
                <button
                  onClick={handleProvisionAll}
                  disabled={isProvisioning}
                  className="px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-xs font-medium flex items-center gap-2"
                >
                  {isProvisioning ? (
                    <>
                      <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      {provisionProgress.current}/{provisionProgress.total}
                    </>
                  ) : (
                    <>Provision All Pending ({pendingCount})</>
                  )}
                </button>
              )}
            </div>

            {isProvisioning && (
              <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800">
                  Provisioning {provisionProgress.currentEmail}... ({provisionProgress.current}/{provisionProgress.total})
                </p>
                <div className="mt-2 h-1.5 bg-amber-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-amber-500 transition-all duration-300"
                    style={{ width: `${(provisionProgress.current / provisionProgress.total) * 100}%` }}
                  />
                </div>
              </div>
            )}

            {isLoadingUsers ? (
              <div className="flex items-center justify-center py-8 text-slate-500">
                <svg className="animate-spin h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Loading users...
              </div>
            ) : users.length === 0 ? (
              <div className="text-center py-8 text-slate-500 text-sm">
                No users added yet. Add emails above to get started.
              </div>
            ) : (
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-slate-200">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Email</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Name</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Role</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Status</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-slate-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {users.map((orgUser) => (
                      <tr key={orgUser.id} className="hover:bg-slate-50">
                        <td className="px-3 py-2 text-sm text-slate-700">{orgUser.email}</td>
                        <td className="px-3 py-2 text-sm text-slate-700">{orgUser.full_name || '—'}</td>
                        <td className="px-3 py-2 text-sm text-slate-600">
                          {ROLE_OPTIONS.find(r => r.value === orgUser.role)?.label || orgUser.role}
                        </td>
                        <td className="px-3 py-2">
                          <UserStatusBadge status={orgUser.provisioning_status} />
                          {orgUser.error_message && (
                            <p className="text-xs text-red-600 mt-0.5">{orgUser.error_message}</p>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right">
                          {orgUser.provisioning_status === 'pending' && (
                            <button
                              onClick={() => handleRemoveUser(orgUser.id)}
                              className="text-xs text-red-600 hover:text-red-800"
                              disabled={isProvisioning}
                            >
                              Remove
                            </button>
                          )}
                          {orgUser.provisioning_status === 'failed' && (
                            <button
                              onClick={() => handleRetryUser(orgUser)}
                              className="text-xs text-amber-600 hover:text-amber-800"
                              disabled={isProvisioning}
                            >
                              Retry
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end pt-4 border-t border-slate-200">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default OrganizationUsersModal
